import { sessionDeadlinePassed } from "./auth/sessionSync";
import {
  isApiEnvelope,
  type ApiEnvelope,
  type ApiError,
  type ErrorDetail,
} from "@shared/errors/envelope";
import type { ErrorCode } from "@shared/errors/codes";
import type {
  CreateDbtJobBody,
  RunDbtJobsBody,
  StartTransformRunResponse,
  DbtJobRunRow,
  DbtJobView,
  UpdateDbtJobBody,
} from "@shared/types/transformations";
import { currentCorrelationId } from "./correlation";
import { logger } from "./logger";
import { currentSurfaceKey } from "./surfaceKey";

/**
 * The 401s that do NOT end the session (round 9, sign-out fix E): each comes with a valid token.
 * `REAUTH_REQUIRED` and `MFA_REQUIRED` (FR-14, admin MFA) ask for a fresher or stronger sign-in,
 * and `INSTANCE_MISMATCH` (B-13) says this person no longer reaches the instance, which a new
 * instance choice answers, not a sign-out. Of the 401s the server sends (`ERROR_STATUS`), that
 * leaves exactly `UNAUTHENTICATED`, `TOKEN_INVALID` and `TOKEN_EXPIRED` to end it; an unknown
 * code, or a 401 that is not an envelope (an ingress page), ends it too: fail closed.
 */
const SESSION_KEEPING_CODES: ReadonlySet<string> = new Set([
  "REAUTH_REQUIRED",
  "MFA_REQUIRED",
  "INSTANCE_MISMATCH",
]);

/**
 * The one HTTP path out of the client (API-STANDARD.md §1 and §4).
 *
 * Every request carries:
 *   - `Authorization: Bearer <token>` from whichever TokenProvider is active
 *     (MSAL in a real environment, the dev issuer locally — LOCAL-DEV §3);
 *   - `x-instance-id`, the instance the user is working in. The server reads
 *     the instance from this header only, never from the URL, and rejects a
 *     header the token does not entitle (401, the IDOR guard);
 *   - `x-surface`, the id-free screen key, so a failure can be described in
 *     product terms;
 *   - `x-correlation-id`, one id per user action, mirrored back on the response
 *     and shown in error toasts.
 *
 * A non-2xx response, or any envelope with `isSuccess: false`, becomes an
 * `ApiClientError`. Callers never inspect `response.ok` themselves.
 */

/** Registered by AuthProvider so the fetch layer has no React coupling. */
let tokenGetter: (() => Promise<string | null>) | null = null;
/** Registered by TenantInstanceProvider. */
let instanceGetter: (() => string | null) | null = null;
/** Registered by AuthProvider: a fresh token was still rejected. */
let sessionExpiredHandler: (() => void) | null = null;
/** Registered by the shell: the instance in the header is no longer reachable (B-13). */
let instanceMismatchHandler: ((message: string) => void) | null = null;

export function setTokenGetter(getter: (() => Promise<string | null>) | null): void {
  tokenGetter = getter;
}

export function setInstanceGetter(getter: (() => string | null) | null): void {
  instanceGetter = getter;
}

export function setSessionExpiredHandler(handler: (() => void) | null): void {
  sessionExpiredHandler = handler;
}

export function setInstanceMismatchHandler(handler: ((message: string) => void) | null): void {
  instanceMismatchHandler = handler;
}

/**
 * #384: the closed 403s the server answers a person who holds a role over an instance that is out
 * of service: it, or its tenant, is disabled, or the tenant deleted (`server/auth/tenantReach.ts`).
 */
export const INSTANCE_REFUSAL_CODES = [
  "TENANT_DELETED",
  "TENANT_DISABLED",
  "INSTANCE_DISABLED",
] as const;

/** The header the in-shell page sends to ask why an address's instance cannot be opened. */
export const INSTANCE_CHECK_HEADER = "x-instance-check";

/**
 * #384 review: a 403 with one of `INSTANCE_REFUSAL_CODES` mid-session is lost access too, so it
 * takes the same recovery as a 401 `INSTANCE_MISMATCH` (`InstanceMismatchRecovery`: `/me` read
 * again, the choice dropped, the in-shell page shown). Without it an open tab kept polling a
 * refused instance, and every poll was another refusal and another `auth.denied` row. The page's
 * own question (the check header) is not lost access: that page is already on screen.
 */
function onRefused(status: number, code: string | undefined, message: string, headers: Headers) {
  if (status !== 403 || code === undefined) return;
  if (!(INSTANCE_REFUSAL_CODES as readonly string[]).includes(code)) return;
  if (headers.get(INSTANCE_CHECK_HEADER) === "1") return;
  instanceMismatchHandler?.(message);
}

/**
 * A 401's consequence, decided by its code (`SESSION_KEEPING_CODES`): the session ends, the
 * instance choice is dropped (`INSTANCE_MISMATCH`, with the server's sentence), or nothing (a
 * step-up the caller's dialog answers). `code` is undefined for a body that is not an envelope.
 */
function onUnauthorized(code: string | undefined, message: string): void {
  if (code === "INSTANCE_MISMATCH") instanceMismatchHandler?.(message);
  else if (code === undefined || !SESSION_KEEPING_CODES.has(code)) sessionExpiredHandler?.();
}

/**
 * A failure the server described in the standard envelope, or a transport
 * failure wearing the same shape so callers have one error type to handle.
 *
 * `message` is always safe to show a user: the server guarantees it
 * (API-STANDARD.md §1) and the transport fallbacks below say what to do next.
 */
export class ApiClientError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly correlationId: string;
  readonly retryable: boolean;
  readonly details: ErrorDetail[];

  constructor(init: {
    code: ErrorCode;
    status: number;
    message: string;
    correlationId: string;
    retryable?: boolean;
    details?: ErrorDetail[];
    cause?: unknown;
  }) {
    super(init.message, { cause: init.cause });
    this.name = "ApiClientError";
    this.code = init.code;
    this.status = init.status;
    this.correlationId = init.correlationId;
    this.retryable = init.retryable ?? false;
    this.details = init.details ?? [];
  }
}

/**
 * #1110 review: the person cancelled a step-up sign-in. Nothing failed, so the screens show it as
 * a plain warning with no reference id (`isStepUpCancelled`); the code stays the refusal's, so a
 * caller that checks for `REAUTH_REQUIRED` or `MFA_REQUIRED` still sees it.
 */
export class StepUpCancelledError extends ApiClientError {
  readonly stepUpCancelled = true;
}

/** The error is a person's own Cancel of a step-up sign-in, not a failure. */
export function isStepUpCancelled(error: unknown): error is StepUpCancelledError {
  return error instanceof StepUpCancelledError;
}

/**
 * The reference id a support conversation starts from, or null for a non-API failure and for a
 * cancelled step-up sign-in (nothing to look up).
 */
export function referenceIdOf(error: unknown): string | null {
  if (isStepUpCancelled(error)) return null;
  return error instanceof ApiClientError ? error.correlationId : null;
}

/**
 * The reference id to PRINT after `detail`, or null when `detail` already
 * names it.
 *
 * Two code paths build the sentence a screen shows. An error answered in the
 * standard envelope carries the server's message and nothing to look up, so the
 * id has to be added. A non-envelope failure — an ingress error page, a dropped
 * connection — is turned into a sentence by the fallback below that already
 * NAMES the id, and a second copy reads as two different ids to whoever is
 * copying one into a support ticket.
 *
 * This lives beside the fallback that writes that sentence, rather than in the
 * component that prints it, because the two have to agree and a screen is not
 * where a reader would look for the reason.
 */
export function referenceIdToShow(detail: string, reference: string | null): string | null {
  return reference && !detail.includes(reference) ? reference : null;
}

/** `detail` with the reference id on the end of it, exactly once. */
export function withReferenceId(detail: string, reference: string | null): string {
  const show = referenceIdToShow(detail, reference);
  return show ? `${detail} Reference id ${show}.` : detail;
}

/**
 * The same sentence, built from whatever a query or mutation threw.
 *
 * For a bar that also offers a retry and a copy button, prefer the `ErrorState`
 * components; this is for the ones whose copy is their own.
 */
export function failureMessage(error: unknown, fallback: string): string {
  const detail = error instanceof Error ? error.message : fallback;
  return withReferenceId(detail, referenceIdOf(error));
}

export interface ApiRequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  /** Override the action's correlation id (e.g. a retried action keeps its own). */
  correlationId?: string;
  /** Skip the Authorization header: the dev login route and the unauthenticated `/api/health`. */
  anonymous?: boolean;
  signal?: AbortSignal;
  /** Extra headers, e.g. `Idempotency-Key` on run start (API-STANDARD.md §6). */
  headers?: Record<string, string>;
  /**
   * Sent even though the idle deadline has passed: only the idle sign-out's own audit call,
   * which is what records that the deadline passed.
   */
  allowAfterIdle?: boolean;
  /**
   * #1110: a privileged write or export with no dialog of its own. A 401 `REAUTH_REQUIRED` or
   * `MFA_REQUIRED` opens the shell's re-authentication dialog (`StepUpHost`) and, once identity
   * is confirmed, sends the SAME request again; nothing was written by the refused one. `"change"`
   * for a save, `"export"` for a download: the dialog's words follow it.
   */
  stepUp?: StepUpKind;
}

/** What the held request does, for the re-authentication dialog's words. */
export type StepUpKind = "change" | "export";

/**
 * Registered by `StepUpHost`: asked when a `stepUp` request is refused for an old or single-factor
 * sign-in. Resolves `true` once the person signed in again (send the request again), `false` when
 * they cancelled (the request fails with a `StepUpCancelledError`).
 */
export interface StepUpHandler {
  /**
   * `again` is true when the SAME request was refused again after the person had already signed
   * in again for it: the dialog then says the sign-in was not refreshed (#1110 review).
   */
  confirm(error: ApiClientError, kind: StepUpKind, again: boolean): Promise<boolean>;
  /** The request sent again went through: the dialog closes, the attempt marker is cleared. */
  succeeded(): void;
}

let stepUpHandler: StepUpHandler | null = null;

export function setStepUpHandler(handler: StepUpHandler | null): void {
  stepUpHandler = handler;
}

/** The sentence a cancelled step-up leaves on the screen that asked for it. */
export const STEP_UP_CANCELLED_MESSAGE: Record<StepUpKind, string> = {
  change: "Sign in again to save this change. Nothing was saved.",
  export: "Sign in again to download this export.",
};

function isStepUpRefusal(error: unknown): error is ApiClientError {
  return (
    error instanceof ApiClientError &&
    (error.code === "REAUTH_REQUIRED" || error.code === "MFA_REQUIRED")
  );
}

/**
 * Runs `send`; on a step-up refusal of a `stepUp` request, asks the shell's dialog and sends
 * again. A cancel fails with a `StepUpCancelledError` carrying the refusal's code and the screen's
 * sentence, so a caller that checks the code still sees `REAUTH_REQUIRED` or `MFA_REQUIRED`.
 */
async function withStepUp<T>(options: ApiRequestOptions, send: () => Promise<T>): Promise<T> {
  const kind = options.stepUp;
  if (!kind || !stepUpHandler) return send();
  let asked = false;
  for (;;) {
    try {
      const result = await send();
      if (asked) stepUpHandler?.succeeded();
      return result;
    } catch (error) {
      if (!isStepUpRefusal(error) || !stepUpHandler) throw error;
      const again = asked;
      asked = true;
      if (!(await stepUpHandler.confirm(error, kind, again))) {
        throw new StepUpCancelledError({
          code: error.code,
          status: error.status,
          message: STEP_UP_CANCELLED_MESSAGE[kind],
          correlationId: error.correlationId,
          cause: error,
        });
      }
    }
  }
}

let idleExpiredHandler: (() => void) | null = null;

/**
 * Round 9 review: called when a request is about to leave after the idle deadline (a tab that
 * slept past it, or one that missed the other tabs' logoff). `AuthContext` ends the session.
 */
export function setIdleExpiredHandler(handler: (() => void) | null): void {
  idleExpiredHandler = handler;
}

async function buildHeaders(options: ApiRequestOptions): Promise<Headers> {
  // No authenticated request leaves after the idle deadline (fail closed, round 9 review): the
  // check is the wall clock against the shared activity time, not a timer that may have slept.
  if (!options.anonymous && !options.allowAfterIdle && sessionDeadlinePassed()) {
    idleExpiredHandler?.();
    throw new ApiClientError({
      code: "TOKEN_EXPIRED",
      status: 0,
      message: "Your session ended after a period of inactivity. Sign in again.",
      correlationId: options.correlationId ?? currentCorrelationId(),
    });
  }
  const headers = new Headers({ accept: "application/json", ...options.headers });
  if (options.body !== undefined) headers.set("content-type", "application/json");

  headers.set("x-correlation-id", options.correlationId ?? currentCorrelationId());

  const surface = currentSurfaceKey();
  if (surface) headers.set("x-surface", surface);

  const instanceId = instanceGetter?.() ?? null;
  // A caller that names its own instance (notification rules, `instanceHeaders(id)`) keeps it (#449).
  if (instanceId && !headers.has("x-instance-id")) headers.set("x-instance-id", instanceId);

  if (!options.anonymous && tokenGetter) {
    const token = await tokenGetter();
    if (token) headers.set("authorization", `Bearer ${token}`);
  }
  return headers;
}

function errorFromEnvelope(error: ApiError, correlationId: string, status: number) {
  return new ApiClientError({
    code: error.code,
    status,
    message: error.message,
    correlationId,
    retryable: error.retryable,
    details: error.details,
  });
}

/**
 * The route without its query string.
 *
 * A query string can carry an identifier: `/dev/login?as=<email>` does, so
 * logging the path verbatim would write a raw email address into a telemetry
 * dimension on any non-envelope failure of that route.
 */
function loggablePath(path: string): string {
  return path.split("?")[0] ?? path;
}

interface RawResult<T> {
  response: Response;
  /** Null for a 204, which carries no body. */
  envelope: ApiEnvelope<T> | null;
  correlationId: string;
}

/** The fetch itself; a transport failure becomes an `ApiClientError`. */
async function sendRequest(
  path: string,
  options: ApiRequestOptions,
  headers: Headers,
  correlationId: string,
): Promise<Response> {
  try {
    return await fetch(path, {
      method: options.method ?? "GET",
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      credentials: "same-origin",
      cache: "no-store",
      signal: options.signal,
    });
  } catch (cause) {
    // Offline, DNS, CORS, aborted. Shaped like an envelope failure so callers
    // and the toast helper have exactly one error type to handle.
    throw new ApiClientError({
      code: "DEPENDENCY_UNAVAILABLE",
      status: 0,
      message: "We could not reach skyData. Check your connection and try again.",
      correlationId,
      retryable: true,
      cause,
    });
  }
}

/** The body parsed as JSON; null for an empty body or one that is not JSON. */
async function readPayload(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    // An ingress error page, not JSON: null, so performRequest's non-envelope
    // branch keeps the reference id.
    return null;
  }
}

/**
 * One guarded request path, shared by both public entry points.
 *
 * Everything that can go wrong before a caller sees data is handled here —
 * transport failure, a failure envelope, a non-envelope error page, unparseable
 * JSON — so there is no second copy to forget one of them in. It always throws
 * an `ApiClientError` carrying the reference id; it never throws a raw
 * `SyntaxError` from `response.json()`.
 */
async function performRequest<T>(path: string, options: ApiRequestOptions): Promise<RawResult<T>> {
  const correlationId = options.correlationId ?? currentCorrelationId();
  const headers = await buildHeaders({ ...options, correlationId });

  const response = await sendRequest(path, options, headers, correlationId);
  const responseCorrelationId = response.headers.get("x-correlation-id") ?? correlationId;

  if (response.status === 204) {
    return { response, envelope: null, correlationId: responseCorrelationId };
  }

  const payload = await readPayload(response);
  const envelope = isApiEnvelope(payload) ? (payload as ApiEnvelope<T>) : null;

  if (envelope && envelope.isSuccess === false && envelope.error) {
    // Only a 401 that says "who are you?" ends the session. A `REAUTH_REQUIRED` 401 (FR-14,
    // SEC-4) has a valid token whose `auth_time` is too old for a privileged write: treating it as
    // an expired session would throw away the form being saved, which the re-authentication
    // dialog exists to avoid. `MFA_REQUIRED` is the same shape, and `INSTANCE_MISMATCH` re-picks
    // the instance (B-13).
    if (response.status === 401) onUnauthorized(envelope.error.code, envelope.error.message);
    onRefused(response.status, envelope.error.code, envelope.error.message, headers);
    throw errorFromEnvelope(
      envelope.error,
      envelope.correlationId ?? responseCorrelationId,
      response.status,
    );
  }

  if (!response.ok || !envelope) {
    if (response.status === 401) onUnauthorized(undefined, "");
    logger.warn("api response was not an envelope", {
      "skydata.surface": currentSurfaceKey(),
      "skydata.status": response.status,
      "skydata.path": loggablePath(path),
    });
    throw new ApiClientError({
      code: response.status === 401 ? "UNAUTHENTICATED" : "INTERNAL",
      status: response.status,
      message: `Something went wrong. Reference id: ${responseCorrelationId}.`,
      correlationId: responseCorrelationId,
      retryable: response.status >= 500,
    });
  }

  return { response, envelope, correlationId: responseCorrelationId };
}

/**
 * Perform a request and unwrap the envelope.
 *
 * 204 responses carry no body and resolve to `undefined`, which is why the
 * return type is `T` rather than `T | null` — a caller that expects nothing
 * types it as `void`.
 */
export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const { response, envelope } = await withStepUp(options, () => performRequest<T>(path, options));
  if (response.status === 204 || !envelope) return undefined as T;
  return envelope.data as T;
}

/** The file types `apiDownload` accepts as a file: CSV, JSON Lines (Session 10) and the run log text (R2.3). */
const DOWNLOAD_CONTENT_TYPES = ["text/csv", "application/x-ndjson", "text/plain"] as const;

/**
 * Fetch a route that answers a FILE rather than an envelope — the audited CSV
 * exports (SEC-16) and the run event exports.
 *
 * It exists so those routes stay on the one HTTP path out of the client: they
 * need the same bearer token, instance header, surface and correlation id as
 * every other request, and a plain `<a href>` carries none of them, so the
 * download would 401. A failure still arrives as an `ApiClientError` with the
 * reference id, because the server answers a normal failure envelope when it
 * refuses.
 *
 * The caller gets the bytes and the server's own filename; turning that into a
 * download is the screen's business, not the transport's.
 */
export async function apiDownload(
  path: string,
  options: ApiRequestOptions = {},
): Promise<{ blob: Blob; filename: string; correlationId: string }> {
  return withStepUp(options, () => downloadOnce(path, options));
}

async function downloadOnce(
  path: string,
  options: ApiRequestOptions,
): Promise<{ blob: Blob; filename: string; correlationId: string }> {
  const correlationId = options.correlationId ?? currentCorrelationId();
  const headers = await buildHeaders({ ...options, correlationId });
  headers.set("accept", `${DOWNLOAD_CONTENT_TYPES.join(", ")}, application/json`);

  let response: Response;
  try {
    response = await fetch(path, {
      method: options.method ?? "GET",
      headers,
      // A GET carries none, but an export that becomes a POST search would
      // otherwise send an empty request and be told, correctly, that it asked
      // for nothing.
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      credentials: "same-origin",
      cache: "no-store",
      signal: options.signal,
    });
  } catch (cause) {
    throw new ApiClientError({
      code: "DEPENDENCY_UNAVAILABLE",
      status: 0,
      message: "We could not reach skyData. Check your connection and try again.",
      correlationId,
      retryable: true,
      cause,
    });
  }

  const responseCorrelationId = response.headers.get("x-correlation-id") ?? correlationId;

  // A 200 is not on its own a file. An ingress that answers a sign-in page, or a
  // route that answers a failure envelope with the wrong status, both arrive
  // here `ok` — and written to disk as `export.csv` they become a corrupt
  // "export" nobody can explain. The content type is what says this is the file:
  // a CSV, or the run events' JSON Lines (Session 10).
  const contentType = (response.headers.get("content-type") ?? "").toLowerCase();
  const isFile = DOWNLOAD_CONTENT_TYPES.some((type) => contentType.startsWith(type));

  if (!response.ok || !isFile) {
    // The refusal is a normal envelope, so re-read it as one and throw the same
    // error type every other caller handles.
    let payload: unknown = null;
    try {
      payload = JSON.parse(await response.text());
    } catch {
      // Not JSON: payload stays null and the generic error below applies.
    }
    const envelope = isApiEnvelope(payload) ? (payload as ApiEnvelope<never>) : null;
    if (response.status === 401) {
      onUnauthorized(envelope?.error?.code, envelope?.error?.message ?? "");
    }
    onRefused(response.status, envelope?.error?.code, envelope?.error?.message ?? "", headers);
    if (envelope?.error) {
      throw errorFromEnvelope(
        envelope.error,
        envelope.correlationId ?? responseCorrelationId,
        response.status,
      );
    }
    logger.warn("download response was not a CSV", {
      "skydata.surface": currentSurfaceKey(),
      "skydata.status": response.status,
      "skydata.path": loggablePath(path),
    });
    throw new ApiClientError({
      code: response.status === 401 ? "UNAUTHENTICATED" : "INTERNAL",
      status: response.status,
      message: `Something went wrong. Reference id: ${responseCorrelationId}.`,
      correlationId: responseCorrelationId,
      retryable: response.status >= 500,
    });
  }

  return {
    blob: await response.blob(),
    filename: filenameFromDisposition(response.headers.get("content-disposition")),
    correlationId: responseCorrelationId,
  };
}

/** `attachment; filename="audit-dev.csv"` → `audit-dev.csv`; a default otherwise. */
function filenameFromDisposition(header: string | null): string {
  const match = header?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  return match?.[1] ?? "export.csv";
}

/** The full envelope, for the caller that needs `meta` (API-STANDARD.md §7). */
export async function apiRequestEnvelope<T>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<ApiEnvelope<T>> {
  const { envelope, correlationId } = await performRequest<T>(path, options);
  if (!envelope) {
    // A 204 has no envelope to hand back; a caller asking for `meta` from a
    // route that answers 204 has the wrong entry point.
    throw new ApiClientError({
      code: "INTERNAL",
      status: 204,
      message: `Something went wrong. Reference id: ${correlationId}.`,
      correlationId,
    });
  }
  return envelope;
}

/* ------------------------------------------------------------------------------------- *
 * Idempotency (API-STANDARD.md §6)
 * ------------------------------------------------------------------------------------- */

/**
 * A key that makes a double-submitted start button cost one run rather than two.
 *
 * A dialog mints ONE key when it opens and reuses it on every retry from that open, so a
 * flaky network or a second click replays the same request and the server answers the run
 * it already created. The fallback is for a runtime without `crypto.randomUUID`.
 */
export function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `key-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** True when the failure is the named envelope code. */
export function isApiErrorCode(error: unknown, code: ErrorCode): error is ApiClientError {
  return error instanceof ApiClientError && error.code === code;
}

/* ------------------------------------------------------------------------------------- *
 * Transformations (FR-62, API-STANDARD.md §7b)
 * ------------------------------------------------------------------------------------- */

/** 2026-09-28 cred-check: the daily credential check's results (`use-credentials.ts`). */
export const CREDENTIALS_PATH = "/api/v2/instances/current/credentials";
export const DBT_JOBS_PATH = "/api/v2/dbt-jobs";
export const DBT_JOBS_GRAPH_PATH = `${DBT_JOBS_PATH}/graph`;
export const DBT_JOBS_RUNS_PATH = `${DBT_JOBS_PATH}/runs`;
export const DBT_JOBS_RUN_NOW_PATH = `${DBT_JOBS_PATH}/run`;

export function dbtJobPath(id: string): string {
  return `${DBT_JOBS_PATH}/${id}`;
}

/**
 * `GET /transformations/:id/history` (session 7): one transformation's per-model outcomes
 * across runs. The cursor is exactly the `meta.nextCursor` the previous page answered; the
 * client never builds one.
 */
export function dbtJobHistoryPath(
  id: string,
  options: { limit?: number; cursor?: string | null; include?: "dependencies" } = {},
): string {
  const params = new URLSearchParams();
  if (options.limit !== undefined) params.set("limit", String(options.limit));
  if (options.cursor) params.set("cursor", options.cursor);
  if (options.include) params.set("include", options.include);
  const query = params.toString();
  const search = query ? `?${query}` : "";
  return `${dbtJobPath(id)}/history${search}`;
}

/** `GET /transformations/runs?limit=`; the limit is in the path so it is in the cache key. */
export function dbtJobRunsQueryPath(limit: number, cursor?: string | null): string {
  const after = cursor ? `&cursor=${encodeURIComponent(cursor)}` : "";
  return `${DBT_JOBS_RUNS_PATH}?limit=${limit}${after}`;
}

/**
 * The transform step of recent runs, newest first, with the page size the server applied and,
 * round 7, the cursor of the next page (null on the last).
 */
export async function listDbtJobRuns(
  limit: number,
  signal?: AbortSignal,
  cursor?: string | null,
): Promise<{ rows: DbtJobRunRow[]; pageSize: number; nextCursor: string | null }> {
  const envelope = await apiRequestEnvelope<DbtJobRunRow[]>(dbtJobRunsQueryPath(limit, cursor), {
    signal,
  });
  return {
    rows: envelope.data ?? [],
    pageSize: envelope.meta?.pageSize ?? limit,
    nextCursor: envelope.meta?.nextCursor ?? null,
  };
}

/** Round 7: one run's transform step whole (`GET /dbt-jobs/runs/:runId`). */
export function dbtJobRunPath(runId: string): string {
  return `${DBT_JOBS_RUNS_PATH}/${encodeURIComponent(runId)}`;
}

/**
 * `POST /transformations/run`: a run with no connections and only the transform step.
 *
 * The key is a parameter rather than minted here so the dialog that owns the open can
 * replay the SAME key on a retry (§6); a key minted per call would make every retry a
 * second run.
 */
export function startTransformRun(
  body: RunDbtJobsBody,
  idempotencyKey: string,
): Promise<StartTransformRunResponse> {
  return apiRequest<StartTransformRunResponse>(DBT_JOBS_RUN_NOW_PATH, {
    method: "POST",
    body,
    headers: { "Idempotency-Key": idempotencyKey },
  });
}

export function createDbtJob(body: CreateDbtJobBody): Promise<DbtJobView> {
  return apiRequest<DbtJobView>(DBT_JOBS_PATH, { method: "POST", body });
}

export function updateDbtJob(id: string, body: UpdateDbtJobBody): Promise<DbtJobView> {
  return apiRequest<DbtJobView>(dbtJobPath(id), { method: "PUT", body });
}

export function deleteDbtJob(id: string): Promise<void> {
  return apiRequest<void>(dbtJobPath(id), { method: "DELETE" });
}

/**
 * The checks `POST /transformations/run` failed, or null when the error is something else.
 *
 * A `TRANSFORM_NOT_READY` refusal carries one `details` entry per failing pre-flight
 * (`destinationType`, `snowflakeAccount`, `dbtRepoUrl`, `dbtJobIds`). The dbt image is
 * not among them: the runner owns it and reports a missing one as a skipped step.
 * The screen renders them as a checklist rather than as one sentence, because each names
 * a different screen to fix it on.
 */
export function transformNotReadyDetails(error: unknown): ErrorDetail[] | null {
  if (!isApiErrorCode(error, "TRANSFORM_NOT_READY")) return null;
  return error.details;
}
